import type { BooLinkRow, ChannelRef, GrantDepth, GrantPreset, GrantScopes, Grantable, PersonRef, ScopeChoice } from "@/lib/api";

// The dashboard's side of boo-backend lib/boo-scopes.ts: presets, the bits of
// scope algebra the screens need, and plain-language summaries. Copy never
// says "AI", "agent" or "container".

export const channelKey = (c: ChannelRef) => `${c.provider}:${c.channelId}`;
export const personKey = (p: PersonRef) => `${p.provider}:${p.providerUserId}`;

const none = <T>(): ScopeChoice<T> => ({ only: [] });

export const NO_SCOPES: GrantScopes = {
  skills: none(),
  memories: { org: false, channels: none(), people: none() },
};

export const PRESETS: Record<GrantPreset, GrantScopes> = {
  essentials: { skills: { all: true }, memories: { org: true, channels: none(), people: none() } },
  team: { skills: { all: true }, memories: { org: true, channels: { all: true }, people: none() } },
  full: { skills: { all: true }, memories: { org: true, channels: { all: true }, people: { all: true } } },
};

export const PRESET_ORDER: GrantPreset[] = ["essentials", "team", "full"];

/** Display name of each depth. */
export const DEPTH_COPY: Record<GrantDepth, { label: string }> = {
  essentials: { label: "Essentials" },
  team: { label: "Team context" },
  full: { label: "Full context" },
  custom: { label: "Custom" },
  none: { label: "Nothing" },
};

// ── Skill items ─────────────────────────────────────────────────────────────
// A skill item is a skill set's registry id (the whole set, including skills
// added later) or "<registry>/<skill>" for one skill in it.

export function skillParts(item: string): { registry: string; skill: string | null } {
  const i = item.indexOf("/");
  return i < 0 ? { registry: item, skill: null } : { registry: item.slice(0, i), skill: item.slice(i + 1) };
}

export const skillItem = (registry: string, skill: string) => `${registry}/${skill}`;

const coversSkill = (x: string, y: string) => x === y || (!x.includes("/") && y.startsWith(`${x}/`));

/** Skill items with any item another one covers left out. */
export function reduceSkills(items: string[]): string[] {
  const unique = [...new Set(items)];
  return unique.filter((y) => !unique.some((x) => x !== y && coversSkill(x, y)));
}

function intersectSkills(a: ScopeChoice<string>, b: ScopeChoice<string>): ScopeChoice<string> {
  if ("all" in a) return "all" in b ? { all: true } : { only: reduceSkills(b.only) };
  if ("all" in b) return { only: reduceSkills(a.only) };
  const both = [...a.only, ...b.only].filter((y) => a.only.some((x) => coversSkill(x, y)) && b.only.some((x) => coversSkill(x, y)));
  return { only: reduceSkills(both) };
}

function sameChoice<T>(a: ScopeChoice<T>, b: ScopeChoice<T>, key: (t: T) => string): boolean {
  if ("all" in a || "all" in b) return "all" in a && "all" in b;
  const ka = a.only.map(key).sort();
  const kb = b.only.map(key).sort();
  return ka.length === kb.length && ka.every((k, i) => k === kb[i]);
}

const id = (s: string) => s;

const reduced = (c: ScopeChoice<string>): ScopeChoice<string> => ("all" in c ? c : { only: reduceSkills(c.only) });

export function sameScopes(a: GrantScopes, b: GrantScopes): boolean {
  return (
    sameChoice(reduced(a.skills), reduced(b.skills), id) &&
    a.memories.org === b.memories.org &&
    sameChoice(a.memories.channels, b.memories.channels, channelKey) &&
    sameChoice(a.memories.people, b.memories.people, personKey)
  );
}

const choiceEmpty = <T>(c: ScopeChoice<T>) => !("all" in c) && c.only.length === 0;

const isChoice = (c: unknown) => !!c && typeof c === "object" && ("all" in c || Array.isArray((c as { only?: unknown }).only));

/** Untyped JSON, such as access event details, that holds a whole GrantScopes. */
export function isGrantScopes(value: unknown): value is GrantScopes {
  if (!value || typeof value !== "object") return false;
  const s = value as Partial<GrantScopes>;
  return isChoice(s.skills) && isChoice(s.memories?.channels) && isChoice(s.memories?.people);
}

export const isEmpty = (s: GrantScopes) =>
  choiceEmpty(s.skills) && !s.memories.org && choiceEmpty(s.memories.channels) && choiceEmpty(s.memories.people);

export function presetOf(s: GrantScopes): GrantDepth {
  if (isEmpty(s)) return "none";
  return PRESET_ORDER.find((p) => sameScopes(s, PRESETS[p])) ?? "custom";
}

function intersectChoice<T>(a: ScopeChoice<T>, b: ScopeChoice<T>, key: (t: T) => string): ScopeChoice<T> {
  if ("all" in a) return "all" in b ? { all: true } : { only: b.only };
  if ("all" in b) return { only: a.only };
  const keep = new Set(b.only.map(key));
  return { only: a.only.filter((x) => keep.has(key(x))) };
}

function unionChoice<T>(a: ScopeChoice<T>, b: ScopeChoice<T>, key: (t: T) => string): ScopeChoice<T> {
  if ("all" in a || "all" in b) return { all: true };
  const seen = new Map<string, T>();
  for (const x of [...a.only, ...b.only]) seen.set(key(x), x);
  return { only: [...seen.values()] };
}

function minusChoice<T>(a: ScopeChoice<T>, b: ScopeChoice<T>, key: (t: T) => string): ScopeChoice<T> {
  if ("all" in b) return { only: [] };
  if ("all" in a) return { all: true };
  const drop = new Set(b.only.map(key));
  return { only: a.only.filter((x) => !drop.has(key(x))) };
}

export function union(a: GrantScopes, b: GrantScopes): GrantScopes {
  return {
    skills: "all" in a.skills || "all" in b.skills ? { all: true } : { only: reduceSkills([...a.skills.only, ...b.skills.only]) },
    memories: {
      org: a.memories.org || b.memories.org,
      channels: unionChoice(a.memories.channels, b.memories.channels, channelKey),
      people: unionChoice(a.memories.people, b.memories.people, personKey),
    },
  };
}

/** What `a` has that `b` doesn't. A whole skill set `b` has only part of still counts as missing. */
export function minus(a: GrantScopes, b: GrantScopes): GrantScopes {
  const skills: ScopeChoice<string> =
    "all" in b.skills
      ? { only: [] }
      : "all" in a.skills
        ? { all: true }
        : { only: reduceSkills(a.skills.only.filter((y) => !(b.skills as { only: string[] }).only.some((x) => coversSkill(x, y)))) };
  return {
    skills,
    memories: {
      org: a.memories.org && !b.memories.org,
      channels: minusChoice(a.memories.channels, b.memories.channels, channelKey),
      people: minusChoice(a.memories.people, b.memories.people, personKey),
    },
  };
}

export const covers = (outer: GrantScopes, inner: GrantScopes) => isEmpty(minus(inner, outer));

export function intersect(a: GrantScopes, b: GrantScopes): GrantScopes {
  return {
    skills: intersectSkills(a.skills, b.skills),
    memories: {
      org: a.memories.org && b.memories.org,
      channels: intersectChoice(a.memories.channels, b.memories.channels, channelKey),
      people: intersectChoice(a.memories.people, b.memories.people, personKey),
    },
  };
}

/** Which scope kinds `s` touches at all. */
export function kindsOf(s: GrantScopes) {
  return {
    skills: !choiceEmpty(s.skills),
    org: s.memories.org,
    channels: !choiceEmpty(s.memories.channels),
    people: !choiceEmpty(s.memories.people),
  };
}

export type ScopeKind = keyof ReturnType<typeof kindsOf>;

export const KIND_COPY: Record<ScopeKind, { label: string; hint: string }> = {
  skills: { label: "Skills", hint: "Skills it can run, using the other Boo's saved secrets." },
  org: { label: "Organization memories", hint: "What it learned about the organization as a whole." },
  channels: { label: "Channel memories", hint: "What it learned in channels and group conversations." },
  people: { label: "One-on-one memories", hint: "What it learned in private conversations with individual people." },
};

/** Scopes covering whole kinds, as a request asks for them. */
export function fromKinds(k: Record<ScopeKind, boolean>): GrantScopes {
  return {
    skills: k.skills ? { all: true } : none(),
    memories: {
      org: k.org,
      channels: k.channels ? { all: true } : none(),
      people: k.people ? { all: true } : none(),
    },
  };
}

const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

/** How much of each kind `s` reaches in `g`: skills, and memories with the channels or people they come from. */
export function kindCounts(s: GrantScopes, g: Grantable) {
  const ch = s.memories.channels;
  const pp = s.memories.people;
  const channels = g.memories.channels.filter((c) => "all" in ch || ch.only.some((x) => channelKey(x) === channelKey(c)));
  const people = g.memories.people.filter((p) => "all" in pp || pp.only.some((x) => personKey(x) === personKey(p)));
  return {
    skills: countIn({ skills: s.skills, memories: NO_SCOPES.memories }, g).skills,
    org: s.memories.org ? g.memories.org.count : 0,
    channelMemories: channels.reduce((n, c) => n + c.count, 0),
    channels: channels.length,
    peopleMemories: people.reduce((n, p) => n + p.count, 0),
    people: people.length,
  };
}

/** "125 from 17 channels". */
export const memoriesFrom = (memories: number, from: number, one: string, many = `${one}s`) => (from === 0 ? "None" : `${num(memories)} from ${plural(from, one, many)}`);

export type ChipOptions = {
  /** The source's grantable, to count what the scopes reach. */
  g?: Grantable | null;
  /** What the counts are out of ("137 of 171 skills"); usually the same grantable. */
  total?: Grantable | null;
  /** How many skills the scopes reach, when known without `g` (a link's own count). */
  skillCount?: number | null;
  /** Also name the kinds left out ("No one-on-one memories"). */
  missing?: boolean;
};

/**
 * One line per kind, the same style everywhere a scope is summarized: "137 of
 * 171 skills", "All 171 skills", "Organization memories", "11 of 17
 * channels", "20 one-on-one memories from 12 people". With only `g`, plain
 * counts ("137 skills"); with neither, words ("All channels", "Some skills").
 * A kind `g` can't account for (it holds only part of the source) falls back
 * to the words.
 */
export function scopeChips(s: GrantScopes, labels: Record<string, string> = {}, o: ChipOptions = {}): string[] {
  const { g, total, skillCount, missing } = o;
  const out: string[] = [];
  const c = g ? kindCounts(s, g) : null;
  const outOf = (n: number, t: number | null, one: string, many = `${one}s`) =>
    t == null ? plural(n, one, many) : n >= t ? `All ${plural(t, one, many)}` : `${num(n)} of ${plural(t, one, many)}`;

  const sk = s.skills;
  const skillsKnown =
    !!g &&
    ("all" in sk ||
      sk.only.every((i) => {
        const { registry, skill } = skillParts(i);
        const r = g.skills.find((x) => x.registry === registry);
        return !!r && (skill === null || r.names.includes(skill));
      }));
  const skillTotal = total ? total.skills.reduce((n, r) => n + r.count, 0) : null;
  if ("all" in sk) {
    const n = skillTotal ?? (skillsKnown ? c!.skills : skillCount) ?? 0;
    out.push(n ? `All ${plural(n, "skill")}` : "All skills");
  } else if (sk.only.length) {
    const items = reduceSkills(sk.only);
    const singles = items.filter((i) => i.includes("/"));
    if (skillsKnown) out.push(outOf(c!.skills, skillTotal, "skill"));
    else if (singles.length === items.length && singles.length <= 2) out.push(singles.map((i) => labels[i] ?? skillParts(i).skill ?? i).join(", "));
    else if (singles.length === items.length) out.push(plural(singles.length, "skill"));
    else if (skillCount != null) out.push(plural(skillCount, "skill"));
    else out.push("Some skills");
  } else if (missing) out.push("No skills");

  if (s.memories.org) out.push("Organization memories");
  else if (missing) out.push("No organization memories");

  const ch = s.memories.channels;
  const chKnown = !!g && ("all" in ch ? g.memories.channels.length > 0 : ch.only.length > 0 && c!.channels === ch.only.length);
  const chTotal = total ? total.memories.channels.length : null;
  if (chKnown) out.push("all" in ch ? `All ${plural(chTotal ?? c!.channels, "channel")}` : outOf(c!.channels, chTotal, "channel"));
  else if ("all" in ch) out.push("All channels");
  else if (ch.only.length) {
    const named = ch.only.map((x) => labels[channelKey(x)]).filter(Boolean);
    out.push(named.length === ch.only.length && named.length <= 2 ? named.join(", ") : plural(ch.only.length, "channel"));
  } else if (missing) out.push("No channel memories");

  const pp = s.memories.people;
  const ppKnown = !!g && ("all" in pp ? g.memories.people.length > 0 : pp.only.length > 0 && c!.people === pp.only.length);
  const ppTotal = total ? total.memories.people.length : null;
  if (ppKnown) {
    const from = ppTotal == null || c!.people >= ppTotal ? plural(c!.people, "person", "people") : `${num(c!.people)} of ${plural(ppTotal, "person", "people")}`;
    out.push(`${plural(c!.peopleMemories, "one-on-one memory", "one-on-one memories")} from ${from}`);
  } else if ("all" in pp) out.push("All one-on-one memories");
  else if (pp.only.length) out.push(`One-on-one memories with ${plural(pp.only.length, "person", "people")}`);
  else if (missing) out.push("No one-on-one memories");
  return out;
}

/** Labels for every channel and person a source has. */
export function labelsOf(g: Grantable | undefined | null): Record<string, string> {
  const out: Record<string, string> = {};
  if (!g) return out;
  for (const r of g.skills) {
    out[r.registry] = r.namespace || r.registry;
    for (const i of r.items ?? []) out[skillItem(r.registry, i.name)] = i.displayName || i.name;
  }
  for (const c of g.memories.channels) out[channelKey(c)] = c.name ?? c.channelId;
  for (const p of g.memories.people) out[personKey(p)] = p.name ?? p.providerUserId;
  return out;
}

/** How many skills and memories `s` reaches in `g`. */
export function countIn(s: GrantScopes, g: Grantable): { skills: number; memories: number } {
  // Skills reached in one skill set: all of them, or the ones named.
  const inSet = (r: { registry: string; count: number; names: string[] }) => {
    if ("all" in s.skills || s.skills.only.includes(r.registry)) return r.count;
    return s.skills.only.filter((i) => skillParts(i).registry === r.registry && r.names.includes(skillParts(i).skill ?? "")).length;
  };
  const ch = s.memories.channels;
  const pp = s.memories.people;
  const hasChannel = (c: ChannelRef) => "all" in ch || ch.only.some((x) => channelKey(x) === channelKey(c));
  const hasPerson = (p: PersonRef) => "all" in pp || pp.only.some((x) => personKey(x) === personKey(p));
  return {
    skills: g.skills.reduce((n, r) => n + inSet(r), 0),
    memories:
      (s.memories.org ? g.memories.org.count : 0) +
      g.memories.channels.filter(hasChannel).reduce((n, c) => n + c.count, 0) +
      g.memories.people.filter(hasPerson).reduce((n, p) => n + p.count, 0),
  };
}

/** "3 skills · 14 memories", or out of `total` ("3 of 9 skills", "all 14 memories"), leaving out what's zero. */
export function countLine(c: { skills: number; memories: number }, total?: { skills: number; memories: number } | null): string {
  const part = (n: number, t: number | undefined, one: string, many: string) =>
    !n ? "" : t === undefined ? plural(n, one, many) : n >= t ? `all ${plural(n, one, many)}` : `${num(n)} of ${plural(t, one, many)}`;
  const parts = [part(c.skills, total?.skills, "skill", "skills"), part(c.memories, total?.memories, "memory", "memories")].filter(Boolean);
  return parts.join(" · ") || "nothing yet";
}

// ── Depth choice ────────────────────────────────────────────────────────────

/** A depth as picked on screen: a preset, or Custom with the kinds ticked. */
export type DepthChoice = { depth: GrantPreset | "custom"; custom: Record<ScopeKind, boolean> };

export const DEFAULT_DEPTH: DepthChoice = {
  depth: "essentials",
  custom: { skills: true, org: true, channels: false, people: false },
};

/** The preset whose kinds are exactly these, if any. */
export function presetOfKinds(kinds: Record<ScopeKind, boolean>): GrantPreset | undefined {
  const all = Object.keys(kinds) as ScopeKind[];
  return PRESET_ORDER.find((p) => all.every((k) => kindsOf(PRESETS[p])[k] === kinds[k]));
}

export function depthScopes(c: DepthChoice): GrantScopes {
  return c.depth === "custom" ? fromKinds(c.custom) : PRESETS[c.depth];
}

export function depthEmpty(c: DepthChoice) {
  return c.depth === "custom" && !(Object.keys(c.custom) as ScopeKind[]).some((k) => c.custom[k]);
}

// ── Link state ──────────────────────────────────────────────────────────────

export type LinkState = "waiting" | "more" | "active" | "partial" | "declined" | "removing" | "connecting" | "failed" | "unreachable";

/** One word for where a link stands, for a pill. */
export function linkState(l: BooLinkRow): LinkState {
  if (l.status === "requested") return "waiting";
  if (l.status === "declined") return "declined";
  if (l.removalPendingSince) return "removing";
  if (l.status !== "approved") return "declined";
  if (l.setUpFailed) return "failed";
  if (!l.setUp) return "connecting";
  if (l.more) return "more";
  if (l.sync?.state === "unreachable" || l.sync?.state === "refused") return "unreachable";
  return l.granted && !covers(l.granted, l.requested) ? "partial" : "active";
}

/** Waiting for the source's admins: a first request, or a request for more on a grant. */
export const needsApproval = (l: BooLinkRow) => l.status === "requested" || (l.status === "approved" && !!l.more);

export type LinkFilter = "waiting" | "active" | "removing" | "declined";

/**
 * Which org-page filter a link falls under. Needs approval comes first, the
 * same test as the count and the "Needs your approval" section, even when the
 * link is also connecting or stopping; the rest follow its status chip
 * (partly approved, connecting and the like count as active).
 */
export function filterOf(l: BooLinkRow): LinkFilter {
  if (needsApproval(l)) return "waiting";
  const s = linkState(l);
  return s === "removing" ? "removing" : s === "declined" ? "declined" : "active";
}

/** Waiting first, then being removed, then active, then the rest; newest first within each. */
export function sortLinks(links: BooLinkRow[]): BooLinkRow[] {
  const rank = (l: BooLinkRow) => (needsApproval(l) ? 0 : l.removalPendingSince ? 1 : l.status === "approved" ? 2 : 3);
  return [...links].sort((a, b) => rank(a) - rank(b) || (b.decidedAt ?? b.requestedAt ?? "").localeCompare(a.decidedAt ?? a.requestedAt ?? ""));
}

/** "Expires in 2 days" once a waiting request is close to running out; null before. */
export function expirySoon(iso: string | null | undefined, now = Date.now()): string | null {
  if (!iso) return null;
  const days = Math.ceil((Date.parse(iso) - now) / 86_400_000);
  if (days > 3) return null;
  return days <= 1 ? "Expires today" : `Expires in ${days} days`;
}

export function shortDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** The source's saved secrets that the skills `s` reaches can read, by name, and how many skills read any. */
export function secretsIn(s: GrantScopes, g: Grantable): { names: string[]; skills: number } {
  const names = new Set<string>();
  let skills = 0;
  for (const r of g.skills) {
    for (const item of r.items ?? []) {
      const reached = "all" in s.skills || s.skills.only.includes(r.registry) || s.skills.only.includes(skillItem(r.registry, item.name));
      if (!reached || item.invalid || item.secrets.length === 0) continue;
      skills += 1;
      for (const secret of item.secrets) names.add(secret);
    }
  }
  return { names: [...names].sort(), skills };
}

/** Big counts with separators: 4,812. */
export const num = (n: number) => n.toLocaleString();

/**
 * A skill's description as one plain line: markdown marks, headings, list
 * bullets and links are dropped, whitespace collapsed. Descriptions come from
 * whoever wrote the skill, so expect anything.
 */
export function plainText(text: string): string {
  return text
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`([^`]*)`/g, "$1")
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/^\s*[-*+]\s+/gm, "")
    .replace(/(\*\*|__|\*|_)(\S[^*_]*?)\1/g, "$2")
    .replace(/\s+/g, " ")
    .trim();
}
